import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Gift, 
  Plus, 
  Pencil, 
  Trash2, 
  Check, 
  X,
  AlertTriangle,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Calendar,
  Euro,
  Tag
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const initialFormData = {
  title: '',
  description: '',
  deposit_amount: '',
  bonus_amount: '',
  badge_text: '',
  end_date: '',
  is_active: true
};

export default function CampaignManager() {
  const { t } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/campaigns`);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingCampaign(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const handleOpenEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      title: campaign.title || '',
      description: campaign.description || '',
      deposit_amount: campaign.deposit_amount?.toString() || '',
      bonus_amount: campaign.bonus_amount?.toString() || '',
      badge_text: campaign.badge_text || '',
      end_date: campaign.end_date ? campaign.end_date.split('T')[0] : '',
      is_active: campaign.is_active ?? true
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.deposit_amount || !formData.bonus_amount) {
      toast.error(t('adminCampaigns.fillRequired'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        deposit_amount: parseFloat(formData.deposit_amount),
        bonus_amount: parseFloat(formData.bonus_amount),
        badge_text: formData.badge_text || null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        is_active: formData.is_active
      };

      if (editingCampaign) {
        await axios.put(`${API_URL}/admin/campaigns/${editingCampaign.id}`, payload);
        toast.success(t('adminCampaigns.campaignUpdated'));
      } else {
        await axios.post(`${API_URL}/admin/campaigns`, payload);
        toast.success(t('adminCampaigns.campaignCreated'));
      }

      setShowModal(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error(error.response?.data?.detail || t('adminCampaigns.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingCampaign) return;

    try {
      await axios.delete(`${API_URL}/admin/campaigns/${deletingCampaign.id}`);
      toast.success(t('adminCampaigns.campaignDeleted'));
      setShowDeleteModal(false);
      setDeletingCampaign(null);
      fetchCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error(t('adminCampaigns.errorDeleting'));
    }
  };

  const toggleActive = async (campaign) => {
    try {
      await axios.put(`${API_URL}/admin/campaigns/${campaign.id}`, {
        is_active: !campaign.is_active
      });
      toast.success(campaign.is_active ? t('adminCampaigns.campaignDeactivated') : t('adminCampaigns.campaignActivated'));
      fetchCampaigns();
    } catch (error) {
      console.error('Error toggling campaign:', error);
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaign-manager-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Gift className="w-7 h-7 text-amber-400" />
            {t('adminCampaigns.title')}
          </h1>
          <p className="text-slate-400 mt-1">
            {t('adminCampaigns.subtitle')}
          </p>
        </div>
        <Button
          onClick={handleOpenCreate}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="create-campaign-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('adminCampaigns.newCampaign')}
        </Button>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10">
          <Gift className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">{t('adminCampaigns.noCampaigns')}</h3>
          <p className="text-slate-400 mb-4">{t('adminCampaigns.createFirst')}</p>
          <Button onClick={handleOpenCreate} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            {t('adminCampaigns.createCampaign')}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign, index) => (
            <div 
              key={campaign.id}
              className={`bg-white/5 rounded-xl border ${campaign.is_active ? 'border-emerald-500/30' : 'border-white/10'} overflow-hidden`}
              data-testid={`admin-campaign-card-${index}`}
            >
              <div className="p-5">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Campaign Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{campaign.title}</h3>
                      {campaign.badge_text && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-semibold">
                          {campaign.badge_text}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        campaign.is_active 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-slate-500/20 text-slate-400'
                      }`}>
                        {campaign.is_active ? t('adminCampaigns.active') : t('adminCampaigns.inactive')}
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm mb-3">{campaign.description}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Euro className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-300">{t('adminCampaigns.deposit')}: <strong>{formatCurrency(campaign.deposit_amount)}</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-emerald-400" />
                        <span className="text-emerald-400">{t('adminCampaigns.bonus')}: <strong>+{formatCurrency(campaign.bonus_amount)}</strong></span>
                      </div>
                      {campaign.end_date && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-400">
                            {t('adminCampaigns.until')}: {new Date(campaign.end_date).toLocaleDateString('pt-PT')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => toggleActive(campaign)}
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-white"
                      title={campaign.is_active ? t('adminCampaigns.deactivate') : t('adminCampaigns.activate')}
                    >
                      {campaign.is_active ? (
                        <ToggleRight className="w-6 h-6 text-emerald-400" />
                      ) : (
                        <ToggleLeft className="w-6 h-6" />
                      )}
                    </Button>
                    <Button
                      onClick={() => handleOpenEdit(campaign)}
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-blue-400"
                      data-testid={`edit-campaign-btn-${index}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => {
                        setDeletingCampaign(campaign);
                        setShowDeleteModal(true);
                      }}
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-red-400"
                      data-testid={`delete-campaign-btn-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#1c262d] border-white/10 max-w-lg" data-testid="campaign-form-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-400" />
              {editingCampaign ? t('adminCampaigns.editCampaign') : t('adminCampaigns.newCampaign')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <Label className="text-slate-300">{t('adminCampaigns.campaignTitle')} *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Bónus de Boas-Vindas"
                className="bg-white/5 border-white/10 text-white mt-1"
                data-testid="campaign-title-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-slate-300">{t('adminCampaigns.campaignDescription')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva a promoção..."
                className="bg-white/5 border-white/10 text-white mt-1 resize-none"
                rows={3}
                data-testid="campaign-description-input"
              />
            </div>

            {/* Amounts */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">{t('adminCampaigns.depositAmount')} *</Label>
                <Input
                  type="number"
                  value={formData.deposit_amount}
                  onChange={(e) => setFormData({ ...formData, deposit_amount: e.target.value })}
                  placeholder="5000"
                  className="bg-white/5 border-white/10 text-white mt-1"
                  min="0"
                  step="100"
                  data-testid="campaign-deposit-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">{t('adminCampaigns.bonusAmount')} *</Label>
                <Input
                  type="number"
                  value={formData.bonus_amount}
                  onChange={(e) => setFormData({ ...formData, bonus_amount: e.target.value })}
                  placeholder="1500"
                  className="bg-white/5 border-white/10 text-white mt-1"
                  min="0"
                  step="50"
                  data-testid="campaign-bonus-input"
                />
              </div>
            </div>

            {/* Badge Text */}
            <div>
              <Label className="text-slate-300">{t('adminCampaigns.badgeText')}</Label>
              <Input
                value={formData.badge_text}
                onChange={(e) => setFormData({ ...formData, badge_text: e.target.value })}
                placeholder={t('adminCampaigns.badgePlaceholder')}
                className="bg-white/5 border-white/10 text-white mt-1"
                data-testid="campaign-badge-input"
              />
            </div>

            {/* End Date */}
            <div>
              <Label className="text-slate-300">{t('adminCampaigns.endDate')}</Label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="bg-white/5 border-white/10 text-white mt-1"
                data-testid="campaign-end-date-input"
              />
            </div>

            {/* Is Active */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  formData.is_active ? 'bg-emerald-500' : 'bg-slate-600'
                }`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  formData.is_active ? 'left-7' : 'left-1'
                }`} />
              </button>
              <Label className="text-slate-300">{t('adminCampaigns.isActive')}</Label>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="border-white/10 text-slate-300 hover:bg-white/5"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="save-campaign-btn"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {editingCampaign ? t('common.save') : t('adminCampaigns.createCampaign')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-[#1c262d] border-white/10 max-w-md" data-testid="delete-campaign-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              {t('adminCampaigns.deleteCampaign')}
            </DialogTitle>
          </DialogHeader>

          <p className="text-slate-300">
            {t('adminCampaigns.deleteConfirm', { title: deletingCampaign?.title })}
          </p>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              className="border-white/10 text-slate-300 hover:bg-white/5"
            >
              <X className="w-4 h-4 mr-2" />
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
